import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiSearch, FiUser } from 'react-icons/fi';
import Loader from '../../components/ui/loader';
import axiosService from '../../utils/axios';
import { useDebounce } from '../../hooks/usePerformanceOptimizations';
import { artistWithImage, artistImageMemory, rememberArtistImages } from './artistImages';
import { popularWithLine, suggestionGenres, suggestionNoteLine, SuggestionNote } from './onboardingLogic';
import { INPUT_CLASS, SectionLabel } from './OnboardingUI';

export interface Artist {
    id?: string | null;
    name?: string;
    displayText?: string;
    url?: string | null;
    image?: string | null;
    genre?: string | null;
    /** Genres this saved artist matches, filled in from suggestion results. */
    matchedGenres?: string[];
}

export interface ArtistGenreLink {
    id: string;
    genres: string[];
}

const SUGGESTIONS_SHOWN = 18;
const SUGGESTION_DEBOUNCE_MS = 350;

function FirstRowLoader() {
    return (
        <div
            className="pointer-events-none absolute inset-x-0 top-1 z-10 flex h-16 items-center justify-center"
            role="status"
            aria-label="Loading artists"
        >
            <Loader size="md" />
        </div>
    );
}

export const artistLabel = (artist: Artist) => artist.displayText || artist.name || '';
const artistKey = (artist: Artist) => artistLabel(artist).toLowerCase();

function spareKeys(visible: Artist[], hidden: Artist[]) {
    const keys = new Set<string>();
    const genres = new Set(visible.map((artist) => artist.genre).filter(Boolean));
    genres.forEach((genre) => {
        const spare = hidden.find((artist) => artist.genre === genre);
        if (spare) keys.add(artistKey(spare));
    });
    return keys;
}

function takeReplacement(bench: Artist[], genre: string | null | undefined, blocked: Set<string>): Artist | undefined {
    const index = bench.findIndex((artist) => {
        const key = artistKey(artist);
        if (!key || blocked.has(key)) return false;
        if (genre && artist.genre && artist.genre !== genre) return false;
        return true;
    });
    if (index < 0) return undefined;
    return bench.splice(index, 1)[0];
}

/** Shape stored on the profile; display-only fields stay out of favArtists. */
export const toFavArtist = ({ id, name, displayText, url }: Artist) => ({ id, name, displayText, url });

interface ArtistTileProps {
    artist: Artist;
    selected: boolean;
    arrived: boolean;
    pending: boolean;
    waitForPhoto: boolean;
    onToggle: (artist: Artist) => void;
    onLoadingChange?: (key: string, loading: boolean) => void;
}

const ArtistTile = memo(function ArtistTile({ artist, selected, arrived, pending, waitForPhoto, onToggle, onLoadingChange }: ArtistTileProps) {
    const imageRef = useRef<HTMLImageElement>(null);
    const [imageReady, setImageReady] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        const node = imageRef.current;
        if (node?.complete && node.naturalWidth > 0) {
            setImageReady(true);
            setImageFailed(false);
            return;
        }
        setImageReady(false);
        setImageFailed(false);
    }, [artist.image]);

    const showImage = Boolean(artist.image) && !imageFailed;
    const grey = pending || (waitForPhoto && showImage && !imageReady);
    const key = artistKey(artist);

    useEffect(() => {
        if (!onLoadingChange) return undefined;
        onLoadingChange(key, grey);
        return () => onLoadingChange(key, false);
    }, [grey, key, onLoadingChange]);

    return (
        <button
            type="button"
            aria-pressed={selected}
            aria-disabled={grey || undefined}
            onClick={() => {
                if (!grey) onToggle(artist);
            }}
            className={`group flex flex-col items-center gap-1.5 p-1 rounded-xl text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-light/40 ${grey ? 'pointer-events-none' : ''}`}
        >
            <span
                className={`relative h-16 w-16 rounded-full transition-all duration-150 ${
                    arrived ? 'artist-arrive' : ''
                } ${
                    grey ? 'grayscale opacity-40' : ''
                } ${
                    selected ? 'ring-[3px] ring-purple-light ring-offset-2' : 'group-hover:ring-2 group-hover:ring-purple-light/40 group-hover:ring-offset-2'
                }`}
            >
                {showImage ? (
                    <img
                        ref={imageRef}
                        src={artist.image || ''}
                        alt=""
                        className="h-full w-full rounded-full object-cover bg-gray-100"
                        onLoad={() => setImageReady(true)}
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <span className="flex h-full w-full items-center justify-center rounded-full bg-purple-light/10 text-purple-light">
                        <FiUser className="h-6 w-6" aria-hidden />
                    </span>
                )}
                {selected && (
                    <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-purple-light text-white border-2 border-white">
                        <FiCheck className="h-3.5 w-3.5" aria-hidden />
                    </span>
                )}
            </span>
            <span className={`text-xs font-semibold leading-tight line-clamp-2 ${selected ? 'text-purple-light' : 'text-gray-700'}`}>
                {artistLabel(artist)}
            </span>
        </button>
    );
});

interface ArtistPickerProps {
    genres: string[];
    selected: Artist[];
    onChange: (artists: Artist[]) => void;
    onGenreLinks?: (links: ArtistGenreLink[]) => void;
    /** Grey each portrait until suggestions, and that portrait's photo, have loaded. */
    dimWhileLoading?: boolean;
}

const ArtistPicker: FC<ArtistPickerProps> = ({ genres, selected, onChange, onGenreLinks, dimWhileLoading = false }) => {
    const [suggestions, setSuggestions] = useState<Artist[]>([]);
    const [genresUsed, setGenresUsed] = useState<string[]>([]);
    const [notes, setNotes] = useState<SuggestionNote[]>([]);
    const [arrived, setArrived] = useState<string[]>([]);
    const [loadedKey, setLoadedKey] = useState('');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Artist[]>([]);
    const [bench, setBench] = useState<Artist[]>([]);
    const [loadingPortraitCount, setLoadingPortraitCount] = useState(0);
    const [fetchedPhotos, setFetchedPhotos] = useState<Artist[]>([]);
    const portraitLoadingKeys = useRef(new Set<string>());
    /** The circle covers the first wave of photos. One replacement after a tap does not. */
    const initialPhotoWave = useRef(true);
    const sawLoadingPortrait = useRef(false);
    const reportPortraitLoading = useCallback((key: string, loadingPortrait: boolean) => {
        const keys = portraitLoadingKeys.current;
        if (loadingPortrait) {
            keys.add(key);
            sawLoadingPortrait.current = true;
        } else {
            keys.delete(key);
        }
        const next = keys.size;
        if (next === 0 && sawLoadingPortrait.current) initialPhotoWave.current = false;
        setLoadingPortraitCount((current) => (current === next ? current : next));
    }, []);
    const debouncedQuery = useDebounce(query, 300);
    const activeGenres = suggestionGenres(genres);
    const genresKey = activeGenres.join(',');
    const seenImages = artistImageMemory();
    const selectedIdsRef = useRef('');
    selectedIdsRef.current = selected.map((artist) => artist.id).filter(Boolean).join(',');
    const onGenreLinksRef = useRef(onGenreLinks);
    onGenreLinksRef.current = onGenreLinks;
    const suggestionDelayRef = useRef(0);
    const photoWaveGenres = useRef<string | null>(null);
    if (photoWaveGenres.current !== genresKey) {
        photoWaveGenres.current = genresKey;
        initialPhotoWave.current = true;
        sawLoadingPortrait.current = false;
    }

    useEffect(() => {
        if (!genresKey) {
            suggestionDelayRef.current = 0;
            setBench([]);
            setSuggestions([]);
            setGenresUsed([]);
            setNotes([]);
            setArrived([]);
            setLoadedKey('');
            return undefined;
        }
        let cancelled = false;
        const delay = suggestionDelayRef.current;
        const timer = setTimeout(async () => {
            suggestionDelayRef.current = SUGGESTION_DEBOUNCE_MS;
            const idPart = selectedIdsRef.current;
            let artists: Artist[] = [];
            let bench: Artist[] = [];
            let responseImages: Artist[] = [];
            let used: string[] = genresKey.split(',');
            let nextNotes: SuggestionNote[] = [];
            let links: ArtistGenreLink[] = [];
            try {
                const artistQuery = idPart ? `&artists=${encodeURIComponent(idPart)}` : '';
                const response = await axiosService.get(`/user/onboarding/artist-suggestions?genres=${encodeURIComponent(genresKey)}${artistQuery}`);
                artists = response.data?.artists || [];
                bench = response.data?.bench || [];
                responseImages = response.data?.artistImages || [];
                used = response.data?.genresUsed?.length ? response.data.genresUsed : used;
                nextNotes = response.data?.notes || [];
                links = (response.data?.selectedInGenres || [])
                    .filter((link: ArtistGenreLink) => link?.id && Array.isArray(link.genres) && link.genres.length);
            } catch {
                artists = [];
                bench = [];
            }
            if (cancelled) return;
            if (links.length) onGenreLinksRef.current?.(links);
            const visible = artists.slice(0, SUGGESTIONS_SHOWN);
            const hidden = [...artists.slice(SUGGESTIONS_SHOWN), ...bench];
            rememberArtistImages(seenImages, artists);
            rememberArtistImages(seenImages, bench);
            rememberArtistImages(seenImages, responseImages);
            setSuggestions(visible);
            setBench(hidden);
            setGenresUsed(used);
            setNotes(nextNotes);
            setArrived([]);
            setLoadedKey(genresKey);
        }, delay);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [genresKey]);

    rememberArtistImages(seenImages, suggestions);
    rememberArtistImages(seenImages, results);
    rememberArtistImages(seenImages, selected);
    rememberArtistImages(seenImages, fetchedPhotos);
    const pictured = (artist: Artist) => artistWithImage(seenImages, artist);
    const missingPhotoKey = selected
        .map((artist) => {
            const id = String(artist.id || '').trim();
            if (!id || pictured(artist).image) return '';
            return id;
        })
        .filter(Boolean)
        .sort()
        .join(',');

    useEffect(() => {
        if (!genresKey || loadedKey !== genresKey || !missingPhotoKey) return undefined;
        let cancelled = false;
        axiosService.get(`/user/onboarding/artist-suggestions?photos=1&genres=${encodeURIComponent(genresKey)}&artists=${encodeURIComponent(missingPhotoKey)}`)
            .then((response) => {
                if (cancelled) return;
                const images = (response.data?.artistImages || []).filter((artist: Artist) => artist?.image);
                if (!images.length) return;
                setFetchedPhotos(images);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [genresKey, loadedKey, missingPhotoKey]);

    const loading = !!genresKey && loadedKey !== genresKey;
    const selectedKeys = new Set(selected.map(artistKey));
    const suggestionKeys = new Set(suggestions.map(artistKey));
    const tiles = [
        ...suggestions,
        ...selected.filter((artist) => !suggestionKeys.has(artistKey(artist))),
    ].map(pictured);
    const portraitsPending = dimWhileLoading && loading;
    const showPortraitLoader = dimWhileLoading && (
        portraitsPending
        || loadingPortraitCount > 1
        || (loadingPortraitCount > 0 && initialPhotoWave.current)
    );
    const reserved = spareKeys(suggestions, bench);
    const moreArtists = bench.filter((artist) => !reserved.has(artistKey(artist)));

    const toggle = (artist: Artist) => {
        const nextArtist = pictured(artist);
        const key = artistKey(nextArtist);
        if (selectedKeys.has(key)) {
            onChange(selected.filter((item) => artistKey(item) !== key));
            return;
        }
        const blocked = new Set([...suggestions, ...selected, nextArtist].map(artistKey));
        const nextBench = bench.slice();
        const replacement = takeReplacement(nextBench, nextArtist.genre, blocked);
        setBench(nextBench);
        setSuggestions((current) => {
            const index = current.findIndex((item) => artistKey(item) === key);
            if (index < 0) return current;
            const next = current.slice();
            if (replacement) next[index] = replacement;
            else next.splice(index, 1);
            return next;
        });
        if (replacement) setArrived([artistKey(replacement)]);
        onChange([...selected, nextArtist]);
    };

    const showMore = () => {
        const next: Artist[] = [];
        const stay: Artist[] = [];
        bench.forEach((artist) => {
            if (next.length < SUGGESTIONS_SHOWN && !reserved.has(artistKey(artist))) next.push(artist);
            else stay.push(artist);
        });
        setSuggestions((current) => [...current, ...next]);
        setBench(stay);
    };

    useEffect(() => {
        const value = debouncedQuery.trim();
        if (!value) {
            setResults([]);
            return undefined;
        }
        let cancelled = false;
        axiosService.get(`/spotify/artists/?query=${encodeURIComponent(value)}&limit=6`)
            .then((response) => {
                if (!cancelled) setResults(response.data?.artists || []);
            })
            .catch(() => {
                if (!cancelled) setResults([]);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedQuery]);

    useEffect(() => {
        if (!arrived.length) return undefined;
        const timer = setTimeout(() => setArrived([]), 5000);
        return () => clearTimeout(timer);
    }, [arrived]);

    const pickResult = (artist: Artist) => {
        const nextArtist = pictured(artist);
        if (!selectedKeys.has(artistKey(nextArtist))) onChange([...selected, nextArtist]);
        setQuery('');
        setResults([]);
    };

    const hint = selected.length ? `${selected.length} picked` : undefined;
    const popular = popularWithLine(genresUsed);
    const noteLine = suggestionNoteLine(notes);

    return (
        <div id="onboarding-artists">
            <SectionLabel hint={hint} note={popular}>Artists you love</SectionLabel>
            {noteLine && <p className="text-sm text-gray-400 mb-3">{noteLine}</p>}
            {loading && tiles.length === 0 ? (
                <div className="relative">
                    <div className="grid grid-cols-3 min-[420px]:grid-cols-4 sm:grid-cols-6 gap-3" aria-busy="true">
                        {Array.from({ length: 6 }, (_, index) => (
                            <div key={index} className="flex flex-col items-center gap-2 p-1">
                                <span className="h-16 w-16 rounded-full bg-gray-100 animate-pulse" />
                                <span className="h-2.5 w-12 rounded bg-gray-100 animate-pulse" />
                            </div>
                        ))}
                    </div>
                    {showPortraitLoader && <FirstRowLoader />}
                </div>
            ) : tiles.length > 0 ? (
                <div className="relative">
                    <div className="grid grid-cols-3 min-[420px]:grid-cols-4 sm:grid-cols-6 gap-x-2 gap-y-3" aria-busy={showPortraitLoader || undefined}>
                        {tiles.map((artist) => (
                            <ArtistTile
                                key={artistKey(artist)}
                                artist={artist}
                                selected={selectedKeys.has(artistKey(artist))}
                                arrived={arrived.includes(artistKey(artist))}
                                pending={portraitsPending}
                                waitForPhoto={dimWhileLoading}
                                onToggle={toggle}
                                onLoadingChange={dimWhileLoading ? reportPortraitLoading : undefined}
                            />
                        ))}
                    </div>
                    {showPortraitLoader && <FirstRowLoader />}
                </div>
            ) : (
                <p className="text-sm text-gray-400">
                    {genres.length ? 'No suggestions for those genres yet. Search for someone below.' : 'Pick a genre above and we will suggest artists.'}
                </p>
            )}
            {moreArtists.length > 0 && (
                <button type="button" className="text-sm font-semibold text-purple-light hover:underline mt-2" onClick={showMore}>
                    Show more artists
                </button>
            )}
            <div className="relative mt-4">
                {results.length > 0 && (
                    <div className="absolute bottom-full left-0 right-0 z-10 mb-2 bg-white border border-gray-100 rounded-xl divide-y divide-gray-100 overflow-hidden shadow-lg" role="listbox">
                        {results.map((artist) => (
                            <button
                                type="button"
                                role="option"
                                aria-selected={selectedKeys.has(artistKey(artist))}
                                key={artist.id || artistKey(artist)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-left bg-white hover:bg-purple-light/5"
                                onClick={() => pickResult(artist)}
                            >
                                {artist.image ? (
                                    <img src={artist.image} alt="" className="h-9 w-9 rounded-full object-cover" />
                                ) : (
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                                        <FiUser className="h-4 w-4" aria-hidden />
                                    </span>
                                )}
                                <span className="font-semibold text-sm flex-1">{artistLabel(artist)}</span>
                                {selectedKeys.has(artistKey(artist)) && <FiCheck className="h-4 w-4 text-purple-light" aria-hidden />}
                            </button>
                        ))}
                    </div>
                )}
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden />
                <input
                    aria-label="Search artists"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Someone missing? Search any artist"
                    className={INPUT_CLASS}
                />
            </div>
        </div>
    );
};

export default ArtistPicker;
